import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import type { RfqHubFileRelease, RfqPackage, RfqProject, TimberRfqDrynessSpec, TimberSupplierStructureRef, VendorWorkType } from '../../types';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import { parseQuoteTotalInput } from '../../utils/rfqQuoteTotal';
import { parseLeadTimeWeeksRangeInput } from '../../utils/rfqLeadTimeWeeks';
import { subscribeToTimberItems, type TimberItem } from '../../utils/materialsDatabaseService';
import {
  allSelectableTimberSpecies,
  rfqProjectTimberSpeciesDryness,
  rfqProjectTimberSupplierSpecies,
} from '../../utils/timberRfqSuppliers';
import { DEFAULT_TIMBER_RFQ_DRYNESS_SPEC } from '../../utils/supplierTimberExport';
import { createStaffSupplierQuoteSubmission } from '../../utils/staffSupplierQuoteUpload';
import { PRE_PORTAL_RELEASE_ID } from '../../utils/timberRfqPrePortalVersion';
import { inviteHasStructureScope } from '../../utils/timberRfqStructureScope';
import {
  normalizeQuotedStructures,
  resolveSubmissionQuotedStructures,
  timberQuoteStructurePickerOptions,
} from '../../utils/timberQuoteStreamKey';
import { loadDealProposalStructures } from '../../utils/loadDealProposalStructures';
import './LegacyQuoteEditModal.css';

export type StaffSupplierQuoteUploadModalProps = {
  open: boolean;
  onClose: () => void;
  project: RfqProject;
  supplierOrgId: string;
  supplierName: string;
  workType: VendorWorkType;
  selectedReleaseId: string | null;
  hubReleases: RfqHubFileRelease[];
  projectPackages: RfqPackage[];
  /** Optional proposal structures when the package/invite has no scope list (common for full-scope CNC). */
  fallbackStructures?: TimberSupplierStructureRef[];
  /** Enabled structures on the deal — used to default partial-scope uploads. */
  dealStructureCount?: number;
  onSuccess?: () => void;
};

function todayDateInputValue(): string {
  return new Date().toISOString().slice(0, 10);
}

const StaffSupplierQuoteUploadModal: React.FC<StaffSupplierQuoteUploadModalProps> = ({
  open,
  onClose,
  project,
  supplierOrgId,
  supplierName,
  workType,
  selectedReleaseId,
  hubReleases,
  projectPackages,
  fallbackStructures: fallbackStructuresProp,
  dealStructureCount = 0,
  onSuccess,
}) => {
  const { user } = useAuth();
  const { publishRfqPackage, createRfqSubmission, updateRfqProject } = useCRM();
  const isTimber = workType === 'timber_rfq';

  const [timberItems, setTimberItems] = useState<TimberItem[]>([]);
  const [species, setSpecies] = useState('');
  const [dryness, setDryness] = useState<TimberRfqDrynessSpec>(DEFAULT_TIMBER_RFQ_DRYNESS_SPEC);
  const [quoteTotal, setQuoteTotal] = useState('');
  const [leadTimeWeeksMin, setLeadTimeWeeksMin] = useState('');
  const [leadTimeWeeksMax, setLeadTimeWeeksMax] = useState('');
  const [receivedDate, setReceivedDate] = useState(todayDateInputValue());
  const [markApproved, setMarkApproved] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStructureIds, setSelectedStructureIds] = useState<string[]>([]);
  const [loadedFallbackStructures, setLoadedFallbackStructures] = useState<TimberSupplierStructureRef[]>([]);

  useEffect(() => subscribeToTimberItems(setTimberItems), []);

  useEffect(() => {
    if (!open || !project.dealId) {
      setLoadedFallbackStructures([]);
      return;
    }
    if (fallbackStructuresProp?.length) {
      setLoadedFallbackStructures(fallbackStructuresProp);
      return;
    }
    let cancelled = false;
    void (async () => {
      const structures = await loadDealProposalStructures(project.dealId, { hubProjectId: project.timberProjectId });
      if (!cancelled) setLoadedFallbackStructures(structures);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, project.dealId, fallbackStructuresProp]);

  const invitedSpecies = useMemo(
    () => rfqProjectTimberSupplierSpecies(project, supplierOrgId),
    [project, supplierOrgId],
  );

  const pkgForOrg = useMemo(() => {
    return (
      projectPackages.find(
        (p) =>
          p.rfqProjectId === project.id
          && p.supplierOrgId === supplierOrgId
          && (!selectedReleaseId
            || selectedReleaseId === PRE_PORTAL_RELEASE_ID
            || p.hubFileReleaseId === selectedReleaseId),
      )
      || projectPackages.find((p) => p.rfqProjectId === project.id && p.supplierOrgId === supplierOrgId)
    );
  }, [project, supplierOrgId, projectPackages, selectedReleaseId]);

  const structureOptions = useMemo(() => {
    const proposalFallback = fallbackStructuresProp?.length
      ? fallbackStructuresProp
      : loadedFallbackStructures;
    return timberQuoteStructurePickerOptions(
      project,
      supplierOrgId,
      pkgForOrg,
      proposalFallback,
    );
  }, [
    project,
    supplierOrgId,
    pkgForOrg,
    fallbackStructuresProp,
    loadedFallbackStructures,
  ]);

  const speciesOptions = useMemo(() => {
    const extras = [...invitedSpecies];
    if (species.trim()) extras.push(species.trim());
    return allSelectableTimberSpecies(timberItems, extras);
  }, [timberItems, invitedSpecies, species]);

  useEffect(() => {
    if (!open) return;
    setSpecies(invitedSpecies.length === 1 ? invitedSpecies[0]! : '');
    setDryness(
      invitedSpecies.length === 1
        ? rfqProjectTimberSpeciesDryness(project, supplierOrgId, invitedSpecies[0]!)
        : DEFAULT_TIMBER_RFQ_DRYNESS_SPEC,
    );
    setQuoteTotal('');
    setLeadTimeWeeksMin('');
    setLeadTimeWeeksMax('');
    setReceivedDate(todayDateInputValue());
    setMarkApproved(false);
    setFile(null);
    setError(null);
    const invite = project.timberSupplierInvites?.find((i) => i.supplierOrgId === supplierOrgId);
    const partialScopeRequest =
      inviteHasStructureScope(invite)
      || (
        dealStructureCount > 0
        && structureOptions.length > 0
        && structureOptions.length < dealStructureCount
      );
    setSelectedStructureIds(
      partialScopeRequest ? structureOptions.map((s) => s.id) : [],
    );
  }, [open, project, supplierOrgId, invitedSpecies, structureOptions, dealStructureCount]);

  useEffect(() => {
    if (!open || !isTimber || !species) return;
    setDryness(rfqProjectTimberSpeciesDryness(project, supplierOrgId, species));
  }, [open, isTimber, species, project, supplierOrgId]);

  if (!open) return null;

  const releaseHint =
    selectedReleaseId && selectedReleaseId !== PRE_PORTAL_RELEASE_ID
      ? hubReleases.find((r) => r.id === selectedReleaseId)?.displayLabel ??
        hubReleases.find((r) => r.id === selectedReleaseId)?.fileName ??
        'Selected release'
      : 'Latest published release (if none selected)';

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) {
      setError('You must be signed in to upload a quote.');
      return;
    }
    if (!file) {
      setError('Attach the supplier quote PDF.');
      return;
    }
    const total = parseQuoteTotalInput(quoteTotal);
    if (!total) {
      setError('Enter a valid total quote amount.');
      return;
    }
    if (isTimber && !species.trim()) {
      setError('Select the species this quote covers.');
      return;
    }

    const leadRange = parseLeadTimeWeeksRangeInput(leadTimeWeeksMin, leadTimeWeeksMax);
    if (leadTimeWeeksMin.trim() || leadTimeWeeksMax.trim()) {
      if (leadRange === null) {
        setError('Lead time must be whole weeks, with min less than or equal to max.');
        return;
      }
    }

    let receivedAt: Date | undefined;
    if (receivedDate.trim()) {
      receivedAt = new Date(`${receivedDate.trim()}T12:00:00`);
      if (Number.isNaN(receivedAt.getTime())) {
        setError('Enter a valid quote received date.');
        return;
      }
    }

    const quotedStructures = resolveSubmissionQuotedStructures({
      selected: selectedStructureIds.length
        ? structureOptions.filter((s) => selectedStructureIds.includes(s.id))
        : undefined,
      packageScope: pkgForOrg?.scopeStructures,
      pickerOptions: structureOptions,
    });
    if (structureOptions.length >= 2 && !quotedStructures?.length) {
      setError('Select at least one structure this quote applies to.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await createStaffSupplierQuoteSubmission(
        { publishRfqPackage, createRfqSubmission, updateRfqProject },
        {
          project,
          orgId: supplierOrgId,
          orgName: supplierName,
          workType,
          selectedReleaseId,
          hubReleases,
          projectPackages,
          staffUserId: user.id,
          staffUserName: user.name || user.email || 'Staff',
        },
        {
          file,
          quoteTotal: total,
          species: isTimber ? species.trim() : undefined,
          dryness: isTimber ? dryness : undefined,
          leadTimeWeeksMin: leadRange?.min,
          leadTimeWeeksMax: leadRange?.max,
          receivedAt,
          markApproved: user?.role === 'staff' ? false : markApproved,
          quotedStructures,
        },
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not upload quote.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lqi-modal-backdrop" role="presentation" onClick={handleClose}>
      <div
        className={`lqi-modal ${isTimber ? 'lqi-modal--timber' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ssqu-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="lqi-modal-head">
          <div>
            <h2 id="ssqu-modal-title">Upload supplier quote PDF</h2>
            <p className="lqi-modal-sub">
              Record a quote received outside the vendor portal for {supplierName}. Release: {releaseHint}.
            </p>
          </div>
          <button type="button" className="lqi-modal-close" onClick={handleClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>

        <form className="lqi-modal-body" noValidate onSubmit={(e) => void handleSubmit(e)}>
              {error && (
            <p className="lqi-error" role="alert">
              {error}
            </p>
          )}

          <label className="lqi-field">
            <span>Supplier</span>
            <input type="text" value={supplierName} disabled readOnly aria-readonly />
          </label>

          {isTimber && (
            <div className="lqi-species-quotes">
              <div className="lqi-species-quotes-head">
                <span className="lqi-species-quotes-title">Quote details</span>
              </div>
              <div className="lqi-species-quote-line">
                <label className="lqi-field lqi-field--inline">
                  <span>Species</span>
                  <select
                    value={species}
                    disabled={busy}
                    required
                    onChange={(e) => setSpecies(e.target.value)}
                  >
                    <option value="">Select species…</option>
                    {speciesOptions.map((sp) => (
                      <option key={sp} value={sp}>
                        {sp}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="lqi-field lqi-field--inline">
                  <span>Dryness</span>
                  <select
                    value={dryness}
                    disabled={busy}
                    onChange={(e) => setDryness(e.target.value as TimberRfqDrynessSpec)}
                  >
                    <option value="green">Green</option>
                    <option value="kiln_dried">Kiln-Dried</option>
                  </select>
                </label>
              </div>
            </div>
          )}

          {structureOptions.length > 0 && (
            <fieldset className="lqi-field" disabled={busy} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
              <legend style={{ fontSize: 12, fontWeight: 600 }}>Structures (optional)</legend>
              <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 400, color: '#64748b' }}>
                Pre-selected when this supplier was sent a partial structure scope. Leave all unchecked only for full assigned scope.
              </p>
              {structureOptions.map((s) => (
                <label key={s.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
                  <input
                    type="checkbox"
                    checked={selectedStructureIds.includes(s.id)}
                    onChange={() =>
                      setSelectedStructureIds((prev) =>
                        prev.includes(s.id) ? prev.filter((id) => id !== s.id) : [...prev, s.id],
                      )
                    }
                  />
                  <span>{s.name || s.id}</span>
                </label>
              ))}
            </fieldset>
          )}

          <label className="lqi-field">
            <span>Total quote ($)</span>
            <input
              type="text"
              inputMode="decimal"
              value={quoteTotal}
              disabled={busy}
              required
              placeholder="e.g. 85000"
              onChange={(e) => setQuoteTotal(e.target.value)}
            />
          </label>

          <div className="lqi-species-quote-line">
            <label className="lqi-field lqi-field--inline">
              <span>Lead min (wk)</span>
              <input
                type="number"
                min={0}
                step={1}
                value={leadTimeWeeksMin}
                disabled={busy}
                placeholder="Optional"
                onChange={(e) => setLeadTimeWeeksMin(e.target.value)}
              />
            </label>
            <label className="lqi-field lqi-field--inline">
              <span>Lead max (wk)</span>
              <input
                type="number"
                min={0}
                step={1}
                value={leadTimeWeeksMax}
                disabled={busy}
                placeholder="Optional"
                onChange={(e) => setLeadTimeWeeksMax(e.target.value)}
              />
            </label>
            <label className="lqi-field lqi-field--inline">
              <span>Received</span>
              <input
                type="date"
                value={receivedDate}
                disabled={busy}
                onChange={(e) => setReceivedDate(e.target.value)}
              />
            </label>
          </div>

          <label className="lqi-field">
            <span>Quote PDF</span>
            <input
              type="file"
              accept=".pdf,application/pdf"
              disabled={busy}
              required
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {file && <span className="lqi-file-name">{file.name}</span>}
          </label>

          {user?.role !== 'staff' && (
            <label className="lqi-check">
              <input
                type="checkbox"
                checked={markApproved}
                disabled={busy}
                onChange={(e) => setMarkApproved(e.target.checked)}
              />
              <span>Mark as approved after upload</span>
            </label>
          )}

          <div className="lqi-modal-actions">
            <button type="button" className="ops-view-btn" disabled={busy} onClick={handleClose}>
              Cancel
            </button>
            <button type="submit" className="ops-new-btn" disabled={busy}>
              {busy ? 'Uploading…' : 'Upload quote'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StaffSupplierQuoteUploadModal;
